import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { removeSupabaseChannelByName, subscribePostgresChannel } from "@/lib/realtime-channel";
import { useToast } from "@/hooks/use-toast";

export interface TaskDeletionRequest {
  id: string;
  task_id: string;
  workspace_id: string;
  requested_by_member_id: string;
  reason: string | null;
  status: "pending" | "approved" | "rejected";
  reviewed_by_member_id: string | null;
  reviewed_at: string | null;
  created_at: string;
  requester?: { display_name: string | null } | null;
  task?: { id: string; title: string; status: string } | null;
}

const REQUEST_SELECT = `id, task_id, workspace_id, requested_by_member_id, reason, status,
  reviewed_by_member_id, reviewed_at, created_at,
  requester:requested_by_member_id(display_name),
  task:task_id(id, title, status)`;

interface UseTaskDeletionRequestsArgs {
  workspaceId: string | null;
  /** Somente gestores carregam a lista de pendentes do workspace. */
  isManager: boolean;
}

export function useTaskDeletionRequests({
  workspaceId,
  isManager,
}: UseTaskDeletionRequestsArgs) {
  const [pending, setPending] = useState<TaskDeletionRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const fetchPending = useCallback(async () => {
    if (!workspaceId || !isManager) {
      setPending([]);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("task_deletion_requests")
        .select(REQUEST_SELECT)
        .eq("workspace_id", workspaceId)
        .eq("status", "pending")
        .order("created_at", { ascending: true });
      if (error) throw error;
      setPending((data ?? []) as unknown as TaskDeletionRequest[]);
    } catch (err: unknown) {
      toast({
        title: "Erro ao carregar solicitações",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [workspaceId, isManager, toast]);

  const fetchPendingRef = useRef(fetchPending);
  fetchPendingRef.current = fetchPending;

  useEffect(() => {
    void fetchPending();
  }, [fetchPending]);

  useEffect(() => {
    if (!workspaceId || !isManager) return;
    const channelName = `task-deletion-requests-${workspaceId}`;
    const channel = subscribePostgresChannel(
      channelName,
      {
        event: "*",
        schema: "public",
        table: "task_deletion_requests",
        filter: `workspace_id=eq.${workspaceId}`,
      },
      () => {
        void fetchPendingRef.current();
      },
    );
    return () => {
      removeSupabaseChannelByName(channelName);
      supabase.removeChannel(channel);
    };
  }, [workspaceId, isManager]);

  const review = useCallback(
    async (requestId: string, approve: boolean) => {
      const { error } = await supabase.rpc("review_task_deletion_request", {
        p_request_id: requestId,
        p_approve: approve,
      });
      if (error) throw error;
      await fetchPending();
    },
    [fetchPending],
  );

  return {
    pending,
    pendingCount: pending.length,
    loading,
    refetch: fetchPending,
    approve: (id: string) => review(id, true),
    reject: (id: string) => review(id, false),
  };
}
